/* eslint-disable */

import SanityImage from '@/components/SanityImage/SanityImage'
import styles from './ProductPageDesktop.module.scss'
import useStore from '@/store'
import { useContext, useEffect, useRef } from 'react'
import Link from '@/components/Link/Link'
import { getProductLinkObject, wait } from '@/utils'
import { ScrollContext } from '@/context/Scroll'
import classNames from 'classnames'
import gsap from 'gsap'
import { Flip } from 'gsap/dist/Flip'
import { useRouter } from 'next/router'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { PRODUCTS_SLUG, TRANSITION_DURATION, TRANSITION_DURATION_MS } from '@/data'
import useCurrentPage from '@/hooks/use-current-page'
import { NEXT_POSITION_ID } from '@/sections/ProductPageUi/ProductPageUi'
import { Fragment } from 'react'
gsap.registerPlugin(Flip)

const IMAGE_CONTAINER_CLASS = styles.imageContainer

export const PRODUCT_PAGE_BREAKPOINTS = {
  tablet: {
    width: 1300,
  },
  xs: {
    width: 400,
  },
}

const HIDE_REGULAR_IMAGES_DURATION = 0.25

const ProductPageDesktop = productPage => {
  const setProductPageData = useStore(state => state.setProductPageData)
  const { onScrollCallback } = useContext(ScrollContext)
  const setProductPageActiveImage = useStore(state => state.setProductPageActiveImage)
  const productGalleryImagesOpen = useStore(state => state.productGalleryImagesOpen)
  const $container = useRef(null)
  const productPageData = useStore(state => state.productPageData)
  const router = useRouter()
  const $nextProductTitleLines = useRef(null)
  const firedIntroAnimation = useRef(false)
  const { prevPathIsProductInnerPage } = useCurrentPage()
  const prevPath = useStore(state => state.prevPath)
  const setNextImageCoords = useStore(state => state.setNextImageCoords)
  const setNextImage = useStore(state => state.setNextImage)
  const { scroll } = useContext(ScrollContext)
  const hasSetProductPageData = useRef(false)
  const isComingFromProductPage = prevPathIsProductInnerPage || !prevPath
  const setForceEnableScrollDisabler = useStore(state => state.setForceEnableScrollDisabler)

  const getRegularImages = () => {
    if (!$container.current) return null
    const $regularImages = $container.current.querySelectorAll(
      `.${styles.regularImages}:not([data-slug="${router?.query?.slug}"])`,
    )
    return $regularImages
  }

  const getNextProductImageContainer = () => {
    const $nextProductImageContainer = document.querySelector(`.${styles.nextProductImageContainer}`)
    if (!$nextProductImageContainer) return null
    return $nextProductImageContainer
  }

  const animateIn = () => {
    const $regularImages = getRegularImages()

    if ($regularImages.length === 0) return

    const $nextPositionEl = document.getElementById(NEXT_POSITION_ID)

    // const flipDuration = 4.4
    const flipDuration = 1.2

    // HACK to make sure the page is scrolled to the top before the animation starts
    // HACK to make sure the page is scrolled to the top before the animation starts
    // HACK to make sure the page is scrolled to the top before the animation starts
    // if ($nextPositionEl) {
    //   const interval = setInterval(() => {
    //     scroll?.scrollTo(0, {
    //       immediate: true,
    //     })
    //     window.scroll(0, 0)
    //   }, 10)

    //   setTimeout(() => {
    //     clearInterval(interval)
    //   }, TRANSITION_DURATION_MS * 2)
    // }
    // END HACK
    // END HACK
    // END HACK

    $regularImages.forEach(async ($el, i) => {
      if (!$el) return

      const $imageContainerInner = $el.querySelector(`.${styles.imageContainer__inner}`)

      if (!$imageContainerInner) return

      if (i === 0 && $nextPositionEl) {
        gsap.set($el, {
          opacity: 0.0001,
        })

        Flip.fit($imageContainerInner, $nextPositionEl, {
          scale: true,
        })

        gsap.set($el, {
          opacity: 1,
        })

        setTimeout(() => {
          gsap.set($nextPositionEl, {
            opacity: 0.0001,
          })

          Flip.fit($imageContainerInner, $el, {
            duration: flipDuration,
            scale: true,
            ease: 'Power4.easeInOut',
            onComplete: () => {
              if (productPage?.nextProduct) {
                setNextImage(productPage.nextProduct.image)
              }
            },
          })
        }, 100)
      } else {
        gsap.to($el, {
          opacity: 1,
          duration: 0.5,
          delay: $nextPositionEl ? flipDuration * 0.75 : 0.05,
          ease: 'Power4.easeOut',
        })
      }
    })
  }

  const animateInBasic = () => {
    const $regularImages = getRegularImages()

    if ($regularImages.length === 0) return

    gsap.fromTo(
      $regularImages,
      {
        opacity: 0,
        y: window.innerWidth * 0.2,
      },
      {
        y: 0,
        opacity: 1,
        delay: TRANSITION_DURATION * 0.5,
        duration: 1.4,
        stagger: 0.1,
        ease: 'Power4.easeOut',
        onComplete: () => {
          if (productPage?.nextProduct) {
            setNextImage(productPage.nextProduct.image)
          }
        },
      },
    )
  }

  useEffect(() => {
    if (!scroll) return
    if (firedIntroAnimation.current) return
    firedIntroAnimation.current = true
    if (isComingFromProductPage) {
      animateIn()
    } else {
      animateInBasic()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll, isComingFromProductPage])

  const getActiveProductOnScroll = () => {
    const $imageContainersRaw = document.querySelectorAll(`.${IMAGE_CONTAINER_CLASS}`)
    const $imageContainers = Array.from($imageContainersRaw)

    let activeIndexDistance = 100000000000
    let activeIndex = 0
    const windowCenter = window.innerHeight / 2
    $imageContainers.forEach(($el, i) => {
      const middleOfElement = $el.getBoundingClientRect().top + $el.offsetHeight / 2
      const fromCenter = Math.abs(middleOfElement - windowCenter)
      if (fromCenter < activeIndexDistance) {
        activeIndexDistance = fromCenter
        activeIndex = i
      }
    })

    const activeImage = productPage?.productData?.images[activeIndex]
    if (activeImage) {
      setProductPageActiveImage({
        ...productPage?.productData?.images[activeIndex],
        index: activeIndex,
      })
    }
  }

  const hideRegularImages = () => {
    const $regularImages = getRegularImages()
    $regularImages.forEach($el => {
      if (!$el) return
      gsap.killTweensOf($el)
      gsap.to($el, {
        opacity: 0,
        duration: HIDE_REGULAR_IMAGES_DURATION,
        ease: 'Power4.easeOut',
      })
    })
  }

  const handleNextProductClick = async () => {
    setForceEnableScrollDisabler(true)

    if ($nextProductTitleLines.current) {
      $nextProductTitleLines.current.animateOut()
    }

    hideRegularImages()

    scroll?.stop()

    setProductPageData(null)

    await wait(HIDE_REGULAR_IMAGES_DURATION * 1000)

    const $nextProductImageContainer = getNextProductImageContainer()

    if (!$nextProductImageContainer) return

    const bounds = $nextProductImageContainer.getBoundingClientRect()

    setNextImageCoords({
      x: bounds.left,
      y: bounds.top,
      width: $nextProductImageContainer.offsetWidth,
      height: $nextProductImageContainer.offsetHeight,
    })

    // setTimeout(() => {
    //   scroll?.scrollTo(0, {
    //     immediate: true,
    //   })
    //   window.scroll(0, 0)
    // }, HIDE_REGULAR_IMAGES_DURATION * 1000)

    // scroll?.start()

    router.push(
      {
        pathname: `/${PRODUCTS_SLUG}/${productPage.nextProduct.slug.current}`,
      },
      undefined,
      { scroll: false },
    )
    // .then(() => {
    //   setProductPageData(null)
    // })
  }

  useEffect(() => {
    if (!$container.current) return
    gsap.to($container.current, {
      opacity: productGalleryImagesOpen ? 0 : 1,
      delay: productGalleryImagesOpen ? 0 : 0.2,
      duration: 0.8,
      ease: 'Power4.easeOut',
    })
  }, [productGalleryImagesOpen])

  useEffect(() => {
    if (!onScrollCallback) return

    const key = `productPage-${productPage?.productData?.slug?.current}`

    setTimeout(() => {
      setProductPageActiveImage({
        ...productPage?.productData?.images[0],
        index: 0,
      })
    }, TRANSITION_DURATION_MS * 0.5)

    onScrollCallback({
      key,
      remove: true,
    })

    onScrollCallback({
      key,
      callback: () => {
        getActiveProductOnScroll()
      },
    })

    return () => {
      onScrollCallback({
        key,
        remove: true,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScrollCallback, productPage])

  useEffect(() => {
    if (hasSetProductPageData.current) return
    if (productPageData?.slug?.current === productPage?.slug?.current) return

    if (router?.query?.slug === productPage?.slug?.current) {
      hasSetProductPageData.current = true
      setProductPageData(productPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPage, router])

  useEffect(() => {
    if (!prevPath) {
      if (productPage?.nextProduct) {
        setNextImage(productPage.nextProduct.image)
      }
    }
  }, [prevPath, productPage])

  const nextProductLink = (
    <Link
      link={getProductLinkObject(productPage.nextProduct.slug.current, productPage.nextProduct.title)}
      ariaLabel={`View ${productPage.nextProduct.title} product page`}
    />
  )

  return (
    <div
      ref={$container}
      className={classNames(
        styles.ProductPage,
        { [styles.productGalleryImagesOpen]: productGalleryImagesOpen },
        {
          [styles.comingFromProductPage]: isComingFromProductPage,
        },
      )}
    >
      {nextProductLink}
      <div className={styles.images}>
        {productPage?.productData?.images?.map((imageObject, i) => (
          <ImageContainer
            imageObject={imageObject}
            key={`${i}-${imageObject.image?.asset?._ref}`}
            className={styles.regularImages}
          />
        ))}
        {productPage?.nextProduct && (
          <Fragment key={`${productPage.nextProduct.slug.current}-${productPage.nextProduct.title}`}>
            {nextProductLink}
            <button
              className={styles.nextProductButton}
              onClick={handleNextProductClick}
              ariaLabel={`View ${productPage.nextProduct.title} product page`}
            >
              <AnimatedTextLines
                querySelector="p"
                autoAnimate={true}
                toAnimate="words"
                ref={$nextProductTitleLines}
              >
                <p className={styles.nextProductTitle}>Next Product</p>
              </AnimatedTextLines>
              <ImageContainer
                imageObject={{
                  image: productPage.nextProduct.image,
                  imageOrientation: productPage.nextProduct.imageOrientation,
                }}
                className={styles.nextProductImageContainer}
                currentPageSlug={productPage?.slug?.current}
                isNextProductImage={true}
              />
            </button>
          </Fragment>
        )}
      </div>
    </div>
  )
}

const ImageContainer = ({ imageObject, className, currentPageSlug, isNextProductImage }) => {
  return (
    <div
      className={classNames(IMAGE_CONTAINER_CLASS, className, {
        [styles.isNextProductImage]: isNextProductImage,
      })}
      data-orientation={imageObject.imageOrientation}
      data-slug={currentPageSlug}
    >
      <div className={styles.imageContainer__inner}>
        {isNextProductImage && (
          <SanityImage
            className={classNames(styles.imageContainer__image, styles.nextProductImage)}
            image={imageObject.image}
            disableLoadedOpacity={true}
            breakpoints={{
              xs: {
                width: 100,
              },
              tablet: {
                width: 200,
              },
            }}
          />
        )}
        <SanityImage
          className={styles.imageContainer__image}
          image={imageObject.image}
          disableLoadedOpacity={true}
          breakpoints={PRODUCT_PAGE_BREAKPOINTS}
        />
      </div>
    </div>
  )
}

ProductPageDesktop.displayName = 'ProductPageDesktop'

export default ProductPageDesktop
